import { useState } from "react";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import { s3baseUrl } from "src/config/config";
import icons from "src/design-system/icons";
import GrPage from "src/design-system/components/GrPage";
import GrPageHeader from "src/design-system/components/GrPageHeader";
import GrMediaCard from "src/design-system/components/GrMediaCard";
import GrButton from "src/design-system/components/GrButton";
import GrIcon from "src/design-system/components/GrIcon";
import GrCard from "src/design-system/components/GrCard";
import GrEmptyState from "src/design-system/components/GrEmptyState";
import GrLoader from "src/design-system/components/GrLoader";
import GrDialog from "src/design-system/components/GrDialog";
import { htmlText } from "../Programmes/programmeHelpers";
import useProgrammeSearch from "../Programmes/useProgrammeSearch";
import {
  ProgrammeLoadMore,
  ProgrammeNoMatch,
  ProgrammeSearchBar,
} from "../Programmes/ProgrammeSearchParts";
import { isFirstRow, libraryGridSx, ownAction } from "./videoHelpers";

/**
 * Videos Library (/videos-library): the member's programmes as cards; one opens that
 * programme's videos.
 */
export default function VidoeLibrary() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  // Programme whose full description is open (View more).
  const [aboutFor, setAboutFor] = useState(null);
  // From the API, 50 at a time; the search runs in the database (Load more for the rest).
  const search = useProgrammeSearch("videosLibraryProgrammes", (error) =>
    enqueueSnackbar(error?.message, { variant: "error" })
  );
  const { isLoading } = search;
  // No programmes at all (not just no match for a search): empty page, no search box.
  const noProgrammes = !isLoading && !search.searching && search.total === 0;

  const handleNavigateDetail = (value) => {
    navigate(`/videos-library/${value?._id?._id}`, {
      state: value,
    });
  };

  return (
    <GrPage>
      <GrPageHeader
        title="Videos Library"
        subtitle="Pick a programme to watch its videos."
        actions={noProgrammes ? null : <ProgrammeSearchBar search={search} />}
      />

      {isLoading ? (
        <GrLoader label="Loading your programmes" />
      ) : noProgrammes ? (
        <GrCard>
          <GrEmptyState
            icon={<GrIcon icon={icons.video} />}
            title="No videos yet"
            text="When your gym gives you a programme, its videos show up here."
          />
        </GrCard>
      ) : (
        <>
          {search.total === 0 ? <ProgrammeNoMatch search={search} /> : null}
          <Box sx={libraryGridSx}>
            {search.visible.map((item, index) => {
              const programme = item._id || {};
              const image =
                programme.program_images?.thumbnail_2 ||
                programme.program_images?.thumbnail_1;
              const title = htmlText(item.title || programme.title) || "Programme";
              return (
                <GrMediaCard
                  eagerImage={isFirstRow(index)}
                  key={programme._id || title}
                  image={image ? s3baseUrl + image : null}
                  imageAlt={title}
                  placeholderIcon={icons.video}
                  title={title}
                  description={programme.short_description}
                  onMoreDescription={() =>
                    setAboutFor({ item, title, text: programme.short_description })
                  }
                  onClick={() => handleNavigateDetail(item)}
                  actions={
                    <GrButton
                      variant="outline"
                      size="sm"
                      startIcon={<GrIcon icon={icons.playArrow} size="sm" />}
                      sx={{ flex: "1 1 0" }}
                      onClick={() => handleNavigateDetail(item)}
                    >
                      Watch videos
                    </GrButton>
                  }
                />
              );
            })}
          </Box>
          <ProgrammeLoadMore search={search} />
        </>
      )}

      <GrDialog
        open={Boolean(aboutFor)}
        onClose={() => setAboutFor(null)}
        title={aboutFor?.title}
        maxWidth={560}
        actions={
          <>
            <GrButton variant="quiet" onClick={() => setAboutFor(null)}>
              Close
            </GrButton>
            <GrButton
              onClick={() => {
                const item = aboutFor?.item;
                setAboutFor(null);
                handleNavigateDetail(item);
              }}
            >
              Watch videos
            </GrButton>
          </>
        }
      >
        <Box component="p" sx={{ m: 0, whiteSpace: "pre-line" }}>
          {aboutFor?.text}
        </Box>
      </GrDialog>
    </GrPage>
  );
}
